import type { MemoryStore } from "./memoryStore";

/** Where a kept demo store lives in the browser. */
export const DEMO_STORE_KEY = "garagevo.demoStore";

/** Fields that are not data (listeners of the running page). */
const SKIP = new Set(["listeners"]);

const replacer = (_key: string, value: unknown) => {
  if (value instanceof Map) return { __map: [...value.entries()] };
  if (value instanceof Set) return { __set: [...value.values()] };
  return value;
};
const reviver = (_key: string, value: unknown) => {
  if (value && typeof value === "object") {
    const v = value as { __map?: [unknown, unknown][]; __set?: unknown[] };
    if (Array.isArray(v.__map)) return new Map(v.__map);
    if (Array.isArray(v.__set)) return new Set(v.__set);
  }
  return value;
};

/** All data of a memory store as text (Maps and Sets kept). */
export function snapshotDemo(store: MemoryStore): string {
  const data: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(store)) if (!SKIP.has(k) && typeof v !== "function") data[k] = v;
  return JSON.stringify(data, replacer);
}

/** Puts a snapshot back into a memory store. */
export function restoreDemo(store: MemoryStore, text: string): void {
  const data = JSON.parse(text, reviver) as Record<string, unknown>;
  for (const [k, v] of Object.entries(data)) if (!SKIP.has(k)) (store as unknown as Record<string, unknown>)[k] = v;
}

/**
 * A demo store that survives a reload and is shared by the tabs of one browser (the browser tests open
 * the customer's quote link in a second tab). Turned on only by VITE_DEMO_PERSIST=true at build time;
 * the normal demo forgets everything on reload. Every call that changes the data writes it to the
 * storage; another tab's change is read back at once, so a stale tab never overwrites it.
 */
export function persistDemo(store: MemoryStore, storage: Storage | undefined, events: Pick<EventTarget, "addEventListener"> | undefined = globalThis): MemoryStore {
  if (!storage) return store;
  let last = "";
  const load = () => {
    try {
      const text = storage.getItem(DEMO_STORE_KEY);
      if (text && text !== last) {
        restoreDemo(store, text);
        last = text;
      }
    } catch {
      // unreadable: start empty
    }
  };
  const save = () => {
    const text = snapshotDemo(store);
    if (text === last) return;
    last = text;
    try {
      storage.setItem(DEMO_STORE_KEY, text);
    } catch {
      // full or blocked: the demo keeps working in memory
    }
  };
  load();
  last = last || snapshotDemo(store);
  events?.addEventListener("storage", (e) => {
    if ((e as StorageEvent).key === DEMO_STORE_KEY) load();
  });
  return new Proxy(store, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver) as unknown;
      if (typeof value !== "function" || prop === "constructor") return value;
      return (...args: unknown[]) => {
        const result = (value as (...a: unknown[]) => unknown).apply(target, args);
        if (result instanceof Promise) return result.finally(save);
        save();
        return result;
      };
    },
  });
}
